import React, { useEffect, useRef } from 'react';
import { InlineSVG } from '../../../../Components/Icons';
import { colors } from '../../../../globalStyles';
import { SaiStudentAiSummary } from '../saiStudentDetail.types';
import {
  EmptyStateP,
  IconWrap,
  InsightsCardHeading,
  InsightsCardSection,
  InsightsGridDiv,
  InsightsModal,
  InsightsSectionDiv,
  InsightsNotGeneratedP,
  ModalBodyDiv,
  ModalHeaderDiv,
  RecommendationsOl,
  SectionBodyDiv,
  SummaryTextP,
} from './AssessmentInsights.styles';
import { Markdown } from './Markdown';

type AssessmentInsightsProps = {
  aiSummary: SaiStudentAiSummary;
};

const SummarySection = ({ children }: { children: React.ReactNode }): React.ReactElement => (
  <InsightsSectionDiv>
    <h3>What's Going On</h3>
    <SectionBodyDiv>{children}</SectionBodyDiv>
  </InsightsSectionDiv>
);

export const InsightsSections = ({ aiSummary }: AssessmentInsightsProps): React.ReactElement => {
  const summary = aiSummary.summary.trim();
  const recommendations = aiSummary.recommendations.filter(
    recommendation => recommendation.trim().length > 0
  );

  return (
    <InsightsGridDiv>
      <SummarySection>
        {summary ? (
          <Markdown>{summary}</Markdown>
        ) : (
          <EmptyStateP>No assessment insights are available for this student.</EmptyStateP>
        )}
      </SummarySection>
      <InsightsSectionDiv>
        <h3>Recommended Next Steps</h3>
        <SectionBodyDiv>
          {recommendations.length ? (
            <RecommendationsOl>
              {recommendations.map((recommendation, index) => (
                <li key={index}>
                  <Markdown inline>{recommendation}</Markdown>
                </li>
              ))}
            </RecommendationsOl>
          ) : (
            <EmptyStateP>No recommended next steps are available for this student.</EmptyStateP>
          )}
        </SectionBodyDiv>
      </InsightsSectionDiv>
    </InsightsGridDiv>
  );
};

const INSIGHTS_CARD_TITLE = 'Insights';

type AssessmentInsightsCardProps = {
  /** Null until the run's summary has been generated, which the card reports rather than leaving out. */
  aiSummary: SaiStudentAiSummary | null;
};

// The staff insight is the summary alone, so the card leaves out the recommendations the modal
// still lays beside it. The summary arrives as plain text rather than the Markdown the modal parses.
export const AssessmentInsightsCard = ({
  aiSummary,
}: AssessmentInsightsCardProps): React.ReactElement => {
  const summary = aiSummary?.summary.trim() ?? '';

  return (
    <InsightsCardSection aria-label={INSIGHTS_CARD_TITLE}>
      <InsightsCardHeading>
        <IconWrap aria-hidden="true">
          <InlineSVG icon="magic-wand" size={22} fill={colors.white} />
        </IconWrap>
        {INSIGHTS_CARD_TITLE}
      </InsightsCardHeading>
      {summary ? (
        <SummarySection>
          <SummaryTextP>{summary}</SummaryTextP>
        </SummarySection>
      ) : (
        <InsightsNotGeneratedP>
          Insights have not yet been generated. Please check back later.
        </InsightsNotGeneratedP>
      )}
    </InsightsCardSection>
  );
};

type AssessmentInsightsModalProps = AssessmentInsightsProps & {
  isOpen: boolean;
  onClose: () => void;
};

export const AssessmentInsightsModal = ({
  aiSummary,
  isOpen,
  onClose,
}: AssessmentInsightsModalProps): React.ReactElement => {
  const headerRef = useRef<HTMLDivElement | null>(null);

  // Opening moves focus into the dialog. Returning it belongs to whoever opened it.
  useEffect(() => {
    if (isOpen) {
      headerRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <InsightsModal isOpen={isOpen} onClose={onClose} size="l" style={{ padding: 0 }}>
      <ModalHeaderDiv ref={headerRef} tabIndex={-1}>
        <IconWrap aria-hidden="true">
          <InlineSVG icon="magic-wand" size={22} fill={colors.white} />
        </IconWrap>
        Insights &amp; Recommended Actions
      </ModalHeaderDiv>
      <ModalBodyDiv>
        {/* MantraModal hides with a class rather than unmounting, so a closed dialog would keep
            parsing and sanitizing the whole narrative on every render of its parent. */}
        {isOpen && <InsightsSections aiSummary={aiSummary} />}
      </ModalBodyDiv>
    </InsightsModal>
  );
};
